import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { Badge } from '@/components/ui/badge'
import { TableCell, TableRow } from '@/components/ui/table'
import { cn } from '@/lib/utils'
import type { PortalSpendRow } from './portalSpend.types'

// Only an organization-billed invoice the viewer may open links; a colleague's invoice never does.
export function portalInvoicePath(row: PortalSpendRow) {
  return row.billed_to_kind === 'organization' && row.openable
    ? `/portal/billing/invoices/${row.invoice_id}`
    : null
}

export function BilledTo({ row }: { row: PortalSpendRow }) {
  if (row.billed_to_kind === 'person' && (row.person_status === 'erased' || row.billed_to === null))
    return <span className="text-muted-foreground">Erased contact</span>
  return (
    <span>
      {row.billed_to}
      {row.person_status === 'former' && (
        <Badge variant="neutral" className="ml-1.5">
          Former
        </Badge>
      )}
    </span>
  )
}

// One invoice row: the number (linked when the viewer may open it), the given cells, the status.
export function PortalInvoiceRow({
  row,
  href,
  children,
}: {
  row: PortalSpendRow
  href: string | null
  children: ReactNode
}) {
  return (
    <TableRow
      className={cn(href && 'cursor-pointer')}
      onClick={
        href
          ? (event) => {
              if ((event.target as Element).closest('a,button')) return
              event.currentTarget.querySelector('a')?.click()
            }
          : undefined
      }
    >
      <TableCell className="whitespace-nowrap">
        {href ? (
          <Link to={href} className="font-medium text-primary hover:underline">
            {row.number}
          </Link>
        ) : (
          <span className="font-medium">{row.number}</span>
        )}
      </TableCell>
      {children}
      <TableCell>
        <Badge variant={row.status === 'paid' ? 'success' : 'warning'}>
          {row.status === 'paid' ? 'Paid' : 'Unpaid'}
        </Badge>
      </TableCell>
    </TableRow>
  )
}
